import AddIcon from '@mui/icons-material/Add'
import { Box, Card, Grid, Paper, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

import { ContentWrapper } from '../../../../../../components/ContentWrapper'
import Helmet from '../../../../../../components/Helmet'
import HeaderContent from '../../../../../../components/Layouts/HeaderContent'
import {
  accountConnections,
  EAccountConnectionStatus,
  EAccountConnectionType,
  IAccount,
  IAccountConnectionsList,
} from '../../../../../../constants/chat'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../constants/errors'
import { LogoTypeSocial } from '../../../../../../constants/image-link'
import PageUrls from '../../../../../../constants/page-urls'
import useAxios, { axiosInstance } from '../../../../../../hooks/useAxios'
import AccountConnectionCard from '../../components/AccountConnectionCard'
import ConnectedAccountCard from '../../components/ConnectedAccountCard'
import { message, Modal } from 'antd'
import { useUser } from '../../../../../../hooks/useUser'
import { ITikTokAccount } from '../../../../../../types/tiktok'
import {
  isAllowConnectShopee,
  isAllowConnectTikTok,
  isAllowFacebook,
  isAllowShopee,
} from '../../../../../../constants/user'
import { IUser } from '../../../../../../types/user'
import { IFacebookAccount } from '../../../../../../types/facebook'
import { IShopeeAccount } from '../../../../../../types/shopee'

const Menus: any[] = []

const ChatIntegrationsListPage = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [popup, setPopup] = useState<any>(null)
  const [params, setParams] = useState<any>(null)

  const { merchant, user } = useUser()

  const [accountConnectionsList, setAccountConnectionsList] =
    useState<IAccountConnectionsList[]>()

  const tiktokAccounts = useAxios<ITikTokAccount[]>({
    method: 'GET',
    url: '/tiktok/merchant/accounts',
  })

  const facebookAccounts = useAxios<IFacebookAccount[]>({
    method: 'GET',
    url: '/facebook/merchant/accounts',
  })

  // ยิงเฉพาะร้านที่เปิด Shopee — บาง environment ยังไม่มี shopee service
  // ยิงไปก็รอ timeout เปล่า ๆ แล้ว SWR retry ซ้ำ
  const shopeeAccounts = useAxios<IShopeeAccount[]>(
    isAllowShopee(merchant)
      ? {
          method: 'GET',
          url: '/shopee/merchant/accounts',
        }
      : null,
  )

  useEffect(() => {
    if (tiktokAccounts.data) {
      const accounts: IAccount[] = []
      tiktokAccounts.data.map((account) => {
        accounts.push({
          image: '',
          name: account.sellerName,
          status: EAccountConnectionStatus.Connected,
          category: 'TikTok Shop',
          objectId: account.objectId,
          id: account.shopId,
        })
      })
      setAccountConnectionsList((prev) => {
        const others = (prev ?? []).filter(
          (item) => item.platform !== EAccountConnectionType.TikTokShop,
        )
        return accounts.length
          ? [
              ...others,
              { platform: EAccountConnectionType.TikTokShop, accounts },
            ]
          : others
      })
    }
  }, [tiktokAccounts.data])

  useEffect(() => {
    if (facebookAccounts.data) {
      const accounts: IAccount[] = []
      facebookAccounts.data.map((account) => {
        accounts.push({
          image: account.imageUrl ?? '',
          name: account.sellerName,
          status: EAccountConnectionStatus.Connected,
          category: 'Facebook',
          objectId: account.objectId,
          id: account.shopId,
        })
      })
      setAccountConnectionsList((prev) => {
        const others = (prev ?? []).filter(
          (item) => item.platform !== EAccountConnectionType.Facebook,
        )
        return accounts.length
          ? [...others, { platform: EAccountConnectionType.Facebook, accounts }]
          : others
      })
    }
  }, [facebookAccounts.data])

  useEffect(() => {
    if (shopeeAccounts.data) {
      const accounts: IAccount[] = []
      shopeeAccounts.data.map((account) => {
        accounts.push({
          image: '',
          name: account.sellerName,
          status: EAccountConnectionStatus.Connected,
          category: 'Shopee',
          objectId: account.objectId,
          id: account.shopId,
        })
      })
      setAccountConnectionsList((prev) => {
        const others = (prev ?? []).filter(
          (item) => item.platform !== EAccountConnectionType.Shopee,
        )
        return accounts.length
          ? [...others, { platform: EAccountConnectionType.Shopee, accounts }]
          : others
      })
    }
  }, [shopeeAccounts.data])

  useEffect(() => {
    if (popup && isPopupOpen) {
      const checkPopupClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopupClosed)
          setIsPopupOpen(false)
          //alert('Popup window closed')
          //setParams(dayjs().unix())
          // mutate()
        }
      }, 500)

      return () => clearInterval(checkPopupClosed)
    }
  }, [popup, isPopupOpen])

  const handleOnConnect = useCallback(
    async (platform: EAccountConnectionType) => {
      switch (platform) {
        case EAccountConnectionType.Facebook:
          try {
            const response = await axiosInstance.get('/facebook/connect', {
              params: {
                redir: `${window.location.origin}${PageUrls.Popup.FacebookPageLists}?display=popup&platform=${platform}`,
              },
            })
            const url = response.data.url ?? ''
            if (url) {
              const popupOptions =
                'width=800,height=640,scrollbars=yes,resizable=no'
              const popupWindow = window.open(url, 'popupWindow', popupOptions)
              if (popupWindow) {
                popupWindow.focus()
                setPopup(popupWindow)
                setIsPopupOpen(true)
              } else {
                alert('Popup was blocked! Please allow popups for this site.')
              }
            }
          } catch (e) {
            console.log('error', e)
            message.error('เชื่อมต่อลิงค์ลูกค้าไม่สำเร็จ')
          }
          break
        case EAccountConnectionType.TikTokShop:
          try {
            const response = await axiosInstance.get('/tiktok/login', {
              params: {
                redir: `${window.location.origin}${PageUrls.Setting.ChatIntegrations}?platform=${platform}`,
              },
            })
            const url = response.data.url ?? ''
            if (url) {
              window.location.href = url
            }
          } catch (e: any) {
            console.log('error', e)
            message.error(
              ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
                'เชื่อมต่อลิงค์ลูกค้าไม่สำเร็จ',
            )
          }
          break
        case EAccountConnectionType.Shopee:
          try {
            const response = await axiosInstance.get('/shopee/login', {
              params: {
                redir: `${window.location.origin}${PageUrls.Setting.ChatIntegrations}?platform=${platform}`,
              },
            })
            const url = response.data.url ?? ''
            if (url) {
              window.location.href = url
            }
          } catch (e: any) {
            console.log('error', e)
            message.error(
              ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
                'เชื่อมต่อลิงค์ลูกค้าไม่สำเร็จ',
            )
          }
          break
      }
    },
    [window],
  )

  const accountPlatformStatus = (platform: EAccountConnectionType) => {
    switch (platform) {
      case EAccountConnectionType.TikTokShop:
        if (!isAllowConnectTikTok(merchant, user)) {
          return 'disabled'
        }
        break
      case EAccountConnectionType.Facebook:
        if (!isAllowFacebook(user as IUser)) {
          return 'disabled'
        }
        break
      case EAccountConnectionType.Shopee:
        if (!isAllowConnectShopee(merchant, user)) {
          return 'disabled'
        }
        break
    }

    const platformsAccount =
      merchant?.config?.platformsAccounts?.[platform] ?? 0

    const accountConnections = accountConnectionsList?.find(
      (item) => item.platform === platform,
    )
    const accounts = accountConnections?.accounts.length ?? 0
    return platformsAccount > accounts ? 'active' : 'disabled'
  }

  return (
    <ContentWrapper>
      <Helmet title="เชื่อมต่อ Social Media" />
      <HeaderContent
        goBack
        title="เชื่อมต่อ Social Media"
        routes={Menus}
        descriptions="เชื่อมต่อ Social Media"
      />
      <Card
        sx={{
          padding: 2,
        }}
      >
        <Typography variant="h5" gutterBottom>
          เชื่อมต่อ Social Media
        </Typography>
        <Box mb={2}>
          {/* <Typography variant="body1">
            จำนวนบัญชีที่เชื่อมต่อในแพ็กเกจของคุณ:{' '}
            {facebookConnected?.length || 0}/100
          </Typography> */}
        </Box>
        {accountConnectionsList?.length ? (
          <>
            {accountConnectionsList.map((data) => (
              <ConnectedAccountCard
                mutate={() => {
                  tiktokAccounts.mutate()
                  facebookAccounts.mutate()
                  shopeeAccounts.mutate()
                }}
                accounts={data.accounts}
                platform={data.platform}
              />
            ))}
          </>
        ) : (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              ยังไม่มีการเชื่อมต่อ Social Media
              คุณสามารถเพิ่มโดยคลิกที่ปุ่มเชื่อมต่อ
            </Typography>
            <Typography variant="body1" color="textSecondary">
              มหาช่องทางที่คุณต้องการ
            </Typography>
          </Paper>
        )}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            รายการ Platform
          </Typography>

          <Grid container spacing={2}>
            {accountConnections.map((account) => (
              <Grid item xs={12} key={account.name}>
                <AccountConnectionCard
                  status={accountPlatformStatus(account.platform)}
                  name={account.name}
                  description={account.description}
                  buttonText={
                    <>
                      <AddIcon /> {account.buttonText}
                    </>
                  }
                  icon={account.icon as LogoTypeSocial}
                  isNew={account.isNew}
                  onClick={() => {
                    Modal.confirm({
                      title: 'ยืนยันการทำรายการ',
                      content: `คุณต้องการเชื่อมต่อ ${account.name} หรือไม่?`,
                      onOk: async () => {
                        handleOnConnect(account.platform)
                      },
                      onCancel() {
                        console.log('Cancel')
                      },
                    })
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Card>
    </ContentWrapper>
  )
}

export default ChatIntegrationsListPage
